import type { RouteLocationRaw } from "vue-router"
import type { IUserAccount } from "@/types/Account.types"
import { ConfigurationState } from "@/static/Configuration"
import { UserRole } from "@/static/User"
import { getAccessPlanCode } from "@/utils/contract"
import {
  appendDialogueSourceToRedirect,
  isDialogueRedirectPath,
  parsePostLoginRedirect,
} from "@/utils/dialogueRedirect"

export const MAGIC_LINK_REDIRECT_ALIASES: Record<string, string> = {
  checkout: "/checkout",
  "editorial-calendar": "/editorial-calendar",
  notifications: "/notifications",
  "mes-echanges": "/mes-echanges",
  "sales-contracts-management": "/sales/contracts",
  home: "/",
  configuration: "/configuration",
  "pilotage-empty-state": "/ma-visibilite/nouvel-etablissement",
}

export interface PostLoginRedirectContext {
  user: IUserAccount | null
  hasDestinationProviders: boolean
  establishmentsCount: number
  accessPlanCode: string | null
  dialogueSource?: string | null
}

function isSaleRole(user: IUserAccount | null): boolean {
  const code = user?.role?.code
  return code === UserRole.sale || code === UserRole.csm
}

function isConfiguredAccount(user: IUserAccount | null): boolean {
  return !user?.account || user.account.configured === true
}

function isDemoProfile(ctx: PostLoginRedirectContext): boolean {
  return (
    ctx.accessPlanCode === "DEMO"
    || ctx.user?.account?.configuration_state === ConfigurationState.DEMO
  )
}

/**
 * Convertit un alias de redirect (URL magic link) en chemin applicatif.
 */
export function normalizeMagicLinkRedirectParam(
  redirect: string | null | undefined
): string | null {
  if (!redirect) {
    return null
  }

  const trimmed = redirect.trim()
  if (!trimmed) {
    return null
  }

  if (MAGIC_LINK_REDIRECT_ALIASES[trimmed]) {
    return MAGIC_LINK_REDIRECT_ALIASES[trimmed]
  }

  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) {
    return trimmed.split("?")[0] || trimmed
  }

  return null
}

/**
 * Destination par défaut selon le profil utilisateur (après chargement des établissements).
 */
export function resolveDefaultPostLoginPath(ctx: PostLoginRedirectContext): string {
  if (isSaleRole(ctx.user)) {
    return MAGIC_LINK_REDIRECT_ALIASES["sales-contracts-management"]
  }

  if (!isConfiguredAccount(ctx.user)) {
    if (isDemoProfile(ctx)) {
      return MAGIC_LINK_REDIRECT_ALIASES["editorial-calendar"]
    }

    const state = ctx.user?.account?.configuration_state
    if (state === ConfigurationState.START_CONNEXION) {
      return "/configuration/connect"
    }
    if (state === ConfigurationState.START_LISTING) {
      return "/configuration/listing"
    }
    if (state === ConfigurationState.START_ESTABLISHMENT || state === ConfigurationState.GOOGLE_CONNECTED) {
      return "/configuration/establishment"
    }

    return MAGIC_LINK_REDIRECT_ALIASES.configuration
  }

  if (ctx.establishmentsCount === 0) {
    if (ctx.hasDestinationProviders) {
      return MAGIC_LINK_REDIRECT_ALIASES["mes-echanges"]
    }

    return MAGIC_LINK_REDIRECT_ALIASES["pilotage-empty-state"]
  }

  if (isDemoProfile(ctx)) {
    return MAGIC_LINK_REDIRECT_ALIASES["editorial-calendar"]
  }

  return MAGIC_LINK_REDIRECT_ALIASES.home
}

function pathOnly(redirectPath: string): string {
  return redirectPath.split("?")[0]
}

/**
 * Vérifie si l'utilisateur peut accéder à la cible demandée.
 */
export function canAccessPostLoginPath(
  redirectPath: string,
  ctx: PostLoginRedirectContext
): boolean {
  const basePath = pathOnly(redirectPath)

  if (basePath === MAGIC_LINK_REDIRECT_ALIASES["sales-contracts-management"] || basePath.startsWith("/sales/")) {
    return isSaleRole(ctx.user)
  }

  if (
    basePath === MAGIC_LINK_REDIRECT_ALIASES["editorial-calendar"]
    || basePath === "/demo-review-responses"
  ) {
    return isDemoProfile(ctx)
  }

  if (basePath === MAGIC_LINK_REDIRECT_ALIASES.checkout) {
    return true
  }

  if (isDialogueRedirectPath(basePath)) {
    return ctx.hasDestinationProviders
  }

  if (basePath === MAGIC_LINK_REDIRECT_ALIASES.notifications) {
    return isConfiguredAccount(ctx.user) || isDemoProfile(ctx)
  }

  if (basePath === MAGIC_LINK_REDIRECT_ALIASES["pilotage-empty-state"]) {
    return ctx.establishmentsCount === 0
  }

  if (basePath === MAGIC_LINK_REDIRECT_ALIASES.configuration || basePath.startsWith("/configuration")) {
    return !isConfiguredAccount(ctx.user) && !isDemoProfile(ctx)
  }

  if (basePath === "/" || basePath === MAGIC_LINK_REDIRECT_ALIASES.home) {
    return isConfiguredAccount(ctx.user) && ctx.establishmentsCount > 0 && !isDemoProfile(ctx)
  }

  return isConfiguredAccount(ctx.user) || isDemoProfile(ctx)
}

/**
 * Valide la cible URL et retourne un chemin sûr (alias ou path).
 */
export function resolvePostLoginPath(
  requestedRedirect: string | null | undefined,
  ctx: PostLoginRedirectContext
): string {
  const normalized = normalizeMagicLinkRedirectParam(requestedRedirect)

  if (normalized && canAccessPostLoginPath(normalized, ctx)) {
    return normalized
  }

  return resolveDefaultPostLoginPath(ctx)
}

function appendQueryParam(path: string, key: string, value: string): string {
  const [pathname, search = ""] = path.split("?")
  const params = new URLSearchParams(search)
  params.set(key, value)
  const query = params.toString()

  return query ? `${pathname}?${query}` : pathname
}

/**
 * Construit la navigation Vue Router à partir des paramètres URL magic link.
 */
export function buildPostLoginNavigation(
  requestedRedirect: string | null | undefined,
  ctx: PostLoginRedirectContext,
  options?: {
    establishmentId?: string | null
    source?: string | null
  }
): RouteLocationRaw {
  let path = resolvePostLoginPath(requestedRedirect, ctx)

  if (options?.establishmentId && pathOnly(path) === MAGIC_LINK_REDIRECT_ALIASES.checkout) {
    path = appendQueryParam(path, "establishmentId", options.establishmentId)
  }

  if (options?.source && isDialogueRedirectPath(path)) {
    path = appendDialogueSourceToRedirect(path, options.source)
  } else if (ctx.dialogueSource && isDialogueRedirectPath(path)) {
    path = appendDialogueSourceToRedirect(path, ctx.dialogueSource)
  }

  return parsePostLoginRedirect(path)
}
